import React from "react";
import { bind } from "decko";

import Util from "./include/Util";
import GameEvent from "./include/GameEvent";
import GoalEventDialog from "./GoalEventDialog";
import PhaseChangeEventDialog from "./PhaseChangeEventDialog";
import MultipleEventDialog from "./MultipleEventDialog";
import SubstitutionEventDialog from "./SubstitutionEventDialog";
import Analytics from "./include/Analytics";

/**
 * This is a pure container component, with only logic.
 * Creates one of multiple Dialogs for publishing a single event
 *
 */
export default class CreateNewEvent extends React.Component {
    constructor (props) {
        super(props);
        this.asyncSaveActive = false;
    }

    componentDidMount () {
        this.componentMountTime = performance.now();
    }

    static eventIsMissingExpectedPlayers (tag) {
        /* Not an error, but returns true if Level 1 *could* have filled out more data than was done */
        switch (tag.action) {
            case "goal": {
                return tag.scorer === undefined;
            }
            case "substitution": {
                return tag["player in"] === undefined || tag["player out"] === undefined;
            }
            case "shot":
            case "red card":
            case "yellow card": {
                return tag.player === undefined;
            }
            default:
                return false;
        }
    }

    @bind
    getVideoTimestamp () {
        // Note: This doesnt take into account when you're in live mode and have seeked backwards
        // Maybe it should? One issue is if the stream stops, it might sorta be recognized as not live
        if (this.props.eventCreationTime instanceof Date) {
            return new Date();
        }
        return this.props.videoCurrentTime;
    }

    @bind
    getVideoAbsoluteTimestamp () {
        // Note: This doesnt take into account when you're in live mode and have seeked backwards
        // Maybe it should? One issue is if the stream stops, it might sorta be recognized as not live
        if (this.props.eventCreationTime instanceof Date) {
            return new Date();
        }
        let absTimestamp = new Date(this.props.stream.start_timestamp);
        absTimestamp.setMilliseconds(
            absTimestamp.getMilliseconds() +
            1000*this.props.videoCurrentTime
        );

        return absTimestamp;
    }

    @bind
    save (tag, score, description, recalculateTimestamp=false) {

        // Avoid accidentally saving duplicate by pressing twice
        if (this.asyncSaveActive) { return; }
        this.asyncSaveActive = true;

        const eventType = tag.action;
        score = score || [this.props.homeTeamGoals, this.props.visitingTeamGoals];

        description = description || Util.createAutomaticTitle(tag, this.props.game, score);

        let timestamp = this.props.eventCreationTime;
        if (recalculateTimestamp) {
            timestamp = this.getVideoTimestamp();
        }

        let newEvent;
        if (timestamp instanceof Date) {
            newEvent = GameEvent.createFromAbsoluteTimestamp(
                this.props.stream,
                timestamp,
                tag,
                score,
                description
            );
        } else {
            newEvent = GameEvent.create(
                this.props.stream,
                timestamp,
                tag,
                score,
                description
            );
        }

        const self = this;
        newEvent.publish(function () {
            self.props.logger("Exported event: " + newEvent.toString());
            self.props.onDone();
            self.asyncSaveActive = false;
        }, function (error) {
            console.log("[ERROR] Saving new event ", error);
            self.props.onError("Error saving new event. " + error);
            self.props.onDone();
            self.asyncSaveActive = false;
        });

        Analytics.streamEvent(this.props.stream, "publish", {
            type: eventType,
            duration: (performance.now() - this.componentMountTime) / 1000,
            missing_fields: CreateNewEvent.eventIsMissingExpectedPlayers(tag),
            live_mode: this.props.liveModeEnabled,
        });
    }

    render () {

        let team = this.props.selectedTeam.name;
        let teamId = this.props.selectedTeam.id;
        let options = [];

        if (this.props.eventType === "start phase") {
            return (
                <PhaseChangeEventDialog
                    hotkeyHandler={this.props.hotkeyHandler}
                    homeTeam={this.props.homeTeam}
                    visitingTeam={this.props.visitingTeam}
                    existingEvents={this.props.existingEvents}
                    getTimestamp={this.getVideoAbsoluteTimestamp}
                    server={this.props.server}
                    onSave={this.save}
                    onCancel={this.props.onDone}
                    game={this.props.game}
                />
            );
        }

        if (this.props.eventType === "goal") {
            return (
                <GoalEventDialog
                    key={this.props.selectedTeam.id}
                    hotkeyHandler={this.props.hotkeyHandler}
                    selectedTeam={this.props.selectedTeam}
                    homeTeam={this.props.homeTeam}
                    visitingTeam={this.props.visitingTeam}
                    homeTeamGoals={this.props.homeTeamGoals}
                    visitingTeamGoals={this.props.visitingTeamGoals}
                    homeTeamKeeper={this.props.homeTeamKeeper}
                    visitingTeamKeeper={this.props.visitingTeamKeeper}
                    lineup={this.props.lineup}
                    playersOnPitch={this.props.playersOnPitch}
                    playersUnavailable={this.props.playersUnavailable}
                    onSave={this.save}
                    onCancel={this.props.onDone}
                />
            );
        }

        if (this.props.eventType === "substitution") {
            return (
                <SubstitutionEventDialog
                    key={this.props.selectedTeam.id}
                    hotkeyHandler={this.props.hotkeyHandler}
                    onSave={this.save}
                    onCancel={this.props.onDone}
                    game={this.props.game}
                    selectedTeam={this.props.selectedTeam}
                    lineup={this.props.lineup}
                    playersOnPitch={this.props.playersOnPitch}
                    playersUnavailable={this.props.playersUnavailable}
                />
            );
        }

        if (this.props.eventType === "shot") {
            options = [{
                key: "off target",
                tag: {
                    action: "shot",
                    "on target": {
                        type: "bool",
                        value: "no"
                    },
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "on target",
                tag: {
                    action: "shot",
                    "on target": {
                        type: "bool",
                        value: "yes"
                    },
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "penalty miss",
                tag: {
                    action: "shot",
                    "shot type": {
                        type: "shot type",
                        value: "penalty"
                    },
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }];
        } else if (this.props.eventType === "card") {
            options = [{
                key: "yellow card",
                tag: {
                    action: "yellow card",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "red card",
                tag: {
                    action: "red card",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }];
        } else if (this.props.eventType === "freekick") {
            options = [{
                key: "free kick",
                tag: {
                    action: "free kick",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "corner",
                tag: {
                    action: "corner",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "offside",
                tag: {
                    action: "offside",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }, {
                key: "penalty",
                tag: {
                    action: "penalty",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }];
        } else if (this.props.eventType === "misc") {
            options = [{
                key: "misc",
                displayTextField: true,
                tag: {action: "misc"}
            }, {
                key: "medical treatment",
                tag: {
                    action: "medical treatment",
                    team: {
                        type: "team",
                        value: team,
                        id: teamId
                    }
                }
            }];
        }

        return (
            <MultipleEventDialog
                key={this.props.selectedTeam.id}
                hotkeyHandler={this.props.hotkeyHandler}
                onSave={this.save}
                onCancel={this.props.onDone}
                game={this.props.game}
                selectedTeam={this.props.selectedTeam}
                homeTeam={this.props.homeTeam}
                visitingTeam={this.props.visitingTeam}
                homeTeamKeeper={this.props.homeTeamKeeper}
                visitingTeamKeeper={this.props.visitingTeamKeeper}
                lineup={this.props.lineup}
                playersOnPitch={this.props.playersOnPitch}
                playersUnavailable={this.props.playersUnavailable}
                options={options}
            />
        );
    }
}

CreateNewEvent.defaultProps = {
};



// WEBPACK FOOTER //
// ./src/js/CreateNewEvent.js